<script setup lang="ts">
import { PV } from "@/widgets";
import { watchEffect } from "vue";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormExt, FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = number;
interface PropsType extends BaseProps<ModelType> {
    formId?: string;
    formSubmit?: boolean;
    placeholder?: string;
    fraction?: boolean;
    plain?: boolean;
    prefix?: string;
    suffix?: string;
    unit?: string;
    min?: number;
    max?: number;
    showLoading?: boolean;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    plain: false,
    fraction: false,
    formSubmit: false,
    min: 0,
    max: Number.MAX_SAFE_INTEGER,
});

const slots = defineSlots<{
    icon?: () => unknown;
}>();

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

watchEffect(() => {
    if (model.value === null) {
        model.value = undefined;
    } else if (model.value !== undefined) {
        if (props.max !== undefined && model.value > props.max) {
            model.value = props.max;
        } else if (props.min !== undefined && model.value < props.min) {
            model.value = props.min;
        }
    }
});

async function catchSubmit() {
    if (props.formSubmit === true) {
        if (props.formId === undefined) return;
        document.forms.namedItem(props.formId)?.requestSubmit();
    } else {
        await actions.onSubmit();
    }
}
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" :unit="props.unit" />

        <template v-if="!state.editMode">
            <PV.IconField v-if="props.showLoading">
                <div class="rns-g-static-input px-1 py-3" :class="{ 'pr-10': state.updatable }">
                    <PV.Skeleton />
                </div>
            </PV.IconField>
            <PV.IconField v-else>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.InputText
                    v-if="model === undefined"
                    class="rns-input-placeholder"
                    disabled
                    fluid
                    :value="props.placeholder"
                />
                <PV.InputNumber
                    v-else
                    v-model="model"
                    class="rns-input-static"
                    disabled
                    fluid
                    locale="id-ID"
                    :maxFractionDigits="props.fraction ? 2 : 0"
                    :prefix="props.prefix"
                    :suffix="props.suffix"
                    :useGrouping="props.plain === false"
                />
                <FormExt :actions="actions" :state="state" />
            </PV.IconField>
        </template>
        <template v-else>
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.InputNumber
                    v-model="model"
                    :disabled="props.disabled"
                    fluid
                    :form="props.formId"
                    :inputId="state.id"
                    :invalid="state.hasError"
                    locale="id-ID"
                    :max="props.max"
                    :maxFractionDigits="props.fraction ? 2 : 0"
                    :min="props.min"
                    :placeholder="props.placeholder ?? `Masukkan ${label}`"
                    :prefix="props.prefix"
                    :required="props.required"
                    :suffix="props.suffix"
                    :useGrouping="props.plain === false"
                    @change="actions.onValidate"
                    @keydown.enter.prevent="catchSubmit"
                />
                <Buttons.ExtReset v-if="state.clearable && state.hasValue" @click="actions.onReset" />
            </PV.IconField>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-input-static :deep(.p-inputnumber-input),
.rns-input-placeholder {
    @apply bg-transparent! shadow-none! px-1! pt-2.25!;
    @apply rns-g-static-input;
}

.rns-input-static :deep(.p-inputnumber-input) {
    @apply text-gray-700!;
}

.rns-input-placeholder {
    @apply text-gray-500!;
}
</style>
